import { formatCurrency, resolveMediaUrl } from "./utils";
import { buildCategoryTree } from "./categories";
import { promotionFor } from "./promotions";
import { openHtmlDocument } from "./docViewer";

const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const SAFE_IMG = /^(https?:\/\/|\/|data:image\/(png|jpe?g|gif|webp);base64,)/i;
const safeImg = (u) => (u && SAFE_IMG.test(String(u).trim()) ? esc(resolveMediaUrl(String(u).trim())) : "");
const netOf = (price, rate) => price / (1 + (Number(rate) || 0) / 100);

export const buildPriceListHtml = ({ products, categories, promos = [], settings = {}, options = {} }) => {
  const { showNet = true, showDescription = false, showImages = false, validUntil = "", title = "LISTA DE PREÇOS", note = "", internal = false } = options;
  const cols = 5 + (showNet ? 1 : 0) + (internal ? 3 : 0);
  const logoRaw = settings.logo_url || settings.logo_file || "";
  const logoSrc = safeImg(logoRaw);
  const name = settings.restaurant_name || settings.business_name || "Empresa";
  const addressLine = [settings.address, settings.city, settings.country === "AO" || !settings.country ? "Angola" : settings.country].filter(Boolean).join(" · ");
  const { roots, childrenOf, parentOf } = buildCategoryTree(categories);
  const today = new Date();

  const row = (p) => {
    const { price, promo, original } = promotionFor(p, promos, parentOf);
    const net = netOf(price, p.tax_rate);
    const cost = Number(p.cost_price) || 0;
    const margin = net > 0 ? ((net - cost) / net) * 100 : 0;
    const stockCell = p.track_stock ? `${Number(p.current_stock ?? 0)}${(p.current_stock ?? 0) <= (p.min_stock ?? 0) ? ' <span class="low">BAIXO</span>' : ""}` : "—";
    return `<tr>
      <td class="mono c ref">${esc((p.product_id || "").replace("prod_", "").slice(-6).toUpperCase())}</td>
      <td>${showImages ? `<div class="pi">${safeImg(p.image_url) ? `<img src="${safeImg(p.image_url)}" onerror="this.style.display='none'"/>` : "<span></span>"}<div>` : ""}
        <strong>${esc(p.name)}</strong>${promo ? ` <span class="promo">PROMO · ${esc(promo.name)}</span>` : ""}
        ${showDescription && p.description ? `<div class="desc">${esc(p.description)}</div>` : ""}
        ${showImages ? "</div></div>" : ""}</td>
      <td class="c">${esc(p.stock_unit || "un.")}</td>
      ${internal ? `<td class="r mono muted">${formatCurrency(cost)}</td><td class="r mono ${margin < 20 ? "neg" : ""}">${margin.toFixed(1)}%</td><td class="c mono">${stockCell}</td>` : ""}
      ${showNet ? `<td class="r mono">${formatCurrency(net)}</td>` : ""}
      <td class="c mono">${Number(p.tax_rate ?? 14)}%</td>
      <td class="r mono price">${promo ? `<s>${formatCurrency(original)}</s> ` : ""}${formatCurrency(price)}</td></tr>`;
  };

  const section = (cat, depth = 0) => {
    const own = products.filter((p) => p.category_id === cat.category_id).sort((a, b) => a.name.localeCompare(b.name));
    const subs = childrenOf[cat.category_id] || [];
    if (!own.length && !subs.some((s) => products.some((p) => p.category_id === s.category_id))) return "";
    return `<tr class="cat d${depth}"><td colspan="${cols}">${depth ? "↳ " : ""}${esc(cat.name)}</td></tr>${own.map(row).join("")}${subs.map((s) => section(s, 1)).join("")}`;
  };
  const uncategorized = products.filter((p) => !categories.some((c) => c.category_id === p.category_id));
  const body = roots.map((r) => section(r)).join("") + (uncategorized.length ? `<tr class="cat d0"><td colspan="${cols}">Outros</td></tr>${uncategorized.map(row).join("")}` : "");
  const totals = internal ? products.reduce((a, p) => { const net = netOf(promotionFor(p, promos, parentOf).price, p.tax_rate); a.cost += Number(p.cost_price) || 0; a.net += net; return a; }, { cost: 0, net: 0 }) : null;

  return `<!DOCTYPE html><html lang="pt"><head><meta charset="utf-8"><title>Lista de Preços — ${esc(name)}</title>
<style>
@page{size:A4;margin:14mm}
*{box-sizing:border-box}
body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:0;font-size:11.5px;line-height:1.4}
@media screen{body{max-width:190mm;margin:16px auto;padding:0 8px}}
.mono{font-family:"Courier New",monospace}.r{text-align:right}.c{text-align:center}
.hd{display:flex;justify-content:space-between;gap:24px;border-bottom:3px solid #111;padding-bottom:12px}
.co{display:flex;gap:14px;align-items:flex-start;max-width:60%}
.logo{width:80px;height:80px;object-fit:contain;border:1px solid #ddd;border-radius:8px;padding:4px;background:#fff}
.co h1{font-size:19px;margin:0 0 4px;font-weight:800}.co p{margin:1px 0;font-size:11px;color:#333}
.doc{text-align:right}.doc h2{margin:0;font-size:18px;letter-spacing:1.5px;font-weight:800}.doc p{margin:2px 0;font-size:11px;color:#333}
table{width:100%;border-collapse:collapse;margin-top:16px}
th{border-top:2px solid #111;border-bottom:2px solid #111;padding:7px 6px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:.5px;background:#f4f4f4}
td{padding:6px;border-bottom:1px solid #e3e3e3;vertical-align:middle}
tr.cat td{background:#111;color:#fff;font-weight:800;text-transform:uppercase;letter-spacing:.6px;font-size:10.5px;padding:6px 8px;border:none}
tr.cat.d1 td{background:#e9e9e9;color:#111;font-size:10px;padding-left:14px}
.ref{color:#666;font-size:10px;width:60px}.price{font-weight:800;font-size:12.5px;white-space:nowrap}.price s{color:#999;font-weight:400;font-size:10.5px;margin-right:4px}
.promo{display:inline-block;background:#d62828;color:#fff;font-size:9px;font-weight:800;padding:1px 5px;border-radius:3px;margin-left:4px;vertical-align:middle}
.desc{color:#555;font-size:10px;margin-top:2px}
.muted{color:#666}.neg{color:#c0392b;font-weight:700}.low{display:inline-block;background:#f59e0b;color:#111;font-size:8.5px;font-weight:800;padding:0 4px;border-radius:3px;margin-left:3px}
.internal{display:inline-block;background:#c0392b;color:#fff;font-weight:800;letter-spacing:1px;font-size:10px;padding:3px 10px;border-radius:4px;margin-top:6px}
.sum{margin-top:12px;display:flex;justify-content:flex-end}.sum table{width:340px;margin:0}.sum td{border:none;padding:3px 6px}.sum .g td{border-top:2px solid #111;font-weight:800}
.pi{display:flex;gap:8px;align-items:center}.pi img{width:34px;height:34px;object-fit:cover;border-radius:4px;border:1px solid #ddd}.pi span{width:34px;height:34px;border-radius:4px;background:#f0f0f0;display:inline-block}
.note{margin-top:14px;border:1px solid #bbb;border-radius:6px;padding:8px 12px;font-size:11px}
.ft{margin-top:22px;border-top:1px solid #bbb;padding-top:8px;text-align:center;font-size:10px;color:#555}
tr{page-break-inside:avoid}
</style></head><body>
<div class="hd">
  <div class="co">
    ${logoSrc ? `<img class="logo" src="${logoSrc}" alt="Logo" onerror="this.style.display='none'"/>` : ""}
    <div>
      <h1>${esc(name)}</h1>
      ${settings.nif ? `<p><b>NIF:</b> ${esc(settings.nif)}</p>` : ""}
      ${addressLine ? `<p>${esc(addressLine)}</p>` : ""}
      ${settings.phone ? `<p><b>Tel:</b> ${esc(settings.phone)}${settings.email ? ` · <b>Email:</b> ${esc(settings.email)}` : ""}</p>` : settings.email ? `<p><b>Email:</b> ${esc(settings.email)}</p>` : ""}
    </div>
  </div>
  <div class="doc">
    <h2>${esc(title)}</h2>
    ${internal ? '<div class="internal">USO INTERNO — NÃO DIVULGAR</div>' : ""}
    <p>Emitida em ${today.toLocaleDateString("pt-PT")}</p>
    ${validUntil ? `<p>Válida até <b>${new Date(validUntil).toLocaleDateString("pt-PT")}</b></p>` : ""}
    <p>${products.length} artigo(s) · Preços em Kz${showNet ? "" : " c/ IVA incluído"}</p>
  </div>
</div>
<table>
  <thead><tr><th class="c">Ref.</th><th>Artigo</th><th class="c">Un.</th>${internal ? '<th class="r">Custo</th><th class="r">Margem</th><th class="c">Stock</th>' : ""}${showNet ? '<th class="r">Preço s/ IVA</th>' : ""}<th class="c">IVA</th><th class="r">Preço c/ IVA</th></tr></thead>
  <tbody>${body || `<tr><td colspan="${cols}" class="c">Sem artigos para os filtros escolhidos</td></tr>`}</tbody>
</table>
${totals ? `<div class="sum"><table>
  <tr><td>Custo total (1 un. de cada artigo)</td><td class="r mono">${formatCurrency(totals.cost)}</td></tr>
  <tr><td>Venda s/ IVA (1 un. de cada artigo)</td><td class="r mono">${formatCurrency(totals.net)}</td></tr>
  <tr class="g"><td>Margem média</td><td class="r mono">${totals.net > 0 ? (((totals.net - totals.cost) / totals.net) * 100).toFixed(1) : "0.0"}%</td></tr>
</table></div>` : ""}
${note ? `<div class="note">${esc(note)}</div>` : ""}
<div class="ft">${internal ? "Documento interno com custos e margens — não entregar a clientes. · " : "Preços em Kwanzas (AOA), IVA incluído no preço final. Sujeitos a alteração sem aviso prévio. · "}${esc(name)}</div>
<script>window.onload=function(){setTimeout(function(){window.print()},300)}</script>
</body></html>`;
};

export const printPriceList = (args) => {
  return openHtmlDocument(buildPriceListHtml(args), { title: "Lista de preços" });
};
